import { useEffect, useState } from "react";
import { Loader2, Trash2, CalendarDays, Clock } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { useSubjects } from "@/lib/buddy";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";

const today = () => new Date().toISOString().slice(0, 10);

const PlanCard = ({ p, onChange, onDelete, subjectName }) => {
  const done = p.steps.filter((s) => s.done).length;
  const toggle = async (i, v) => onChange((await http.patch(`/buddy/plans/${p.id}/steps/${i}`, { done: v })).data);
  return (
    <article className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6 rise" data-testid={`plan-${p.id}`}>
      <div className="flex justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-[#2C4A3E]">{p.title}</h3>
          <p className="text-xs text-stone-500 mt-1">{subjectName || "General"}{p.due_date && <> · <CalendarDays className="inline w-3 h-3" /> due {p.due_date}</>}</p>
        </div>
        <button onClick={() => onDelete(p.id)} data-testid={`plan-delete-${p.id}`} className="text-stone-400 hover:text-red-500 h-fit" aria-label="Delete plan"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-3 mt-4"><Progress value={(100 * done) / (p.steps.length || 1)} className="h-2" /><span className="text-xs text-stone-500 whitespace-nowrap" data-testid={`plan-progress-${p.id}`}>{done}/{p.steps.length}</span></div>
      <ul className="mt-4 space-y-2">
        {p.steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 rounded-xl px-3 py-2 hover:bg-[#F7F3EB]">
            <Checkbox checked={s.done} onCheckedChange={(v) => toggle(i, !!v)} data-testid={`plan-step-${p.id}-${i}`} className="mt-0.5" />
            <span className={`text-sm flex-1 ${s.done ? "line-through text-stone-400" : "text-stone-700"}`}>{s.text}</span>
            <span className="text-[11px] text-stone-400 whitespace-nowrap"><Clock className="inline w-3 h-3" /> {s.minutes}m{s.due_date && ` · ${s.due_date.slice(5)}`}</span>
          </li>
        ))}
      </ul>
      {p.buddy_tip && <p className="mt-4 text-sm font-display italic text-[#C87D53]">{p.buddy_tip}</p>}
    </article>
  );
};

export const AssignmentPlanner = () => {
  const { buddy } = useProfile();
  const subjects = useSubjects();
  const [f, setF] = useState({ title: "", subject_id: "", due_date: "", details: "" });
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  useEffect(() => { http.get("/buddy/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  const name = (id) => subjects.find((s) => s.id === id)?.name;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await http.post("/buddy/plans", { ...f, subject_id: f.subject_id || null, due_date: f.due_date || null });
      setPlans((p) => [r.data, ...p]); setF({ title: "", subject_id: "", due_date: "", details: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const replace = (np) => setPlans((ps) => ps.map((p) => (p.id === np.id ? np : p)));
  const remove = async (id) => { await http.delete(`/buddy/plans/${id}`); setPlans((ps) => ps.filter((p) => p.id !== id)); };
  const input = "px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm";

  return (
    <div data-testid="assignment-planner">
      <form onSubmit={create} className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6 grid sm:grid-cols-2 gap-3">
        <p className="sm:col-span-2 font-display text-xl text-[#2C4A3E]">What's the assignment? {buddy.name} will break it into small steps.</p>
        <input required minLength={2} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Book report on Hatchet" data-testid="plan-title-input" className={`${input} sm:col-span-2`} />
        <select value={f.subject_id} onChange={(e) => setF({ ...f, subject_id: e.target.value })} data-testid="plan-subject-select" className={input}>
          <option value="">Subject (optional)</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type="date" min={today()} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="plan-due-input" className={input} />
        <textarea value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} rows={3} maxLength={2000} placeholder="What does your teacher want? Paste the instructions…" data-testid="plan-details-input" className={`${input} sm:col-span-2`} />
        <button disabled={busy} data-testid="plan-submit-btn" className="sm:col-span-2 justify-self-start inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#2C4A3E] text-white disabled:opacity-50 hover:bg-[#1E352B] transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? "Planning…" : "Make my plan"}
        </button>
      </form>
      <div className="grid lg:grid-cols-2 gap-5 mt-8" data-testid="plans-list">
        {plans.map((p) => <PlanCard key={p.id} p={p} onChange={replace} onDelete={remove} subjectName={name(p.subject_id)} />)}
      </div>
      {!plans.length && <p className="text-stone-500 text-sm mt-6" data-testid="plans-empty">No assignments planned yet.</p>}
    </div>
  );
};
